import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';

type CoreRow = any;

@Component({
  selector: 'app-controle-core',
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingIndicatorComponent],
  templateUrl: './controle-core.component.html',
  styleUrl: './controle-core.component.css'
})
export class ControleCoreComponent implements OnInit {
  readonly Math = Math;
  readonly pageSize = 15;
  readonly money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  readonly dates = new Intl.DateTimeFormat('pt-BR');

  rows: CoreRow[] = [];
  loading = false;
  error = '';
  search = '';
  statusFilter = '';
  page = 1;
  detail: CoreRow | null = null;
  filtersOpen = false;
  actionModal: 'charge' | 'return' | null = null;
  actionRow: CoreRow | null = null;
  chargeForm = { codtipoper: '1598', descTipoper: 'DEVOLUÇÃO COMPRA - RETORNO DE CORE', codtipvenda: '13', descTipvenda: 'TED', codnat: '1010202', descNat: 'RECEITA COM PRESTACAO SERVIÇO', codcencus: '202000', descCencus: 'VENDAS', tipodtcot: 'N' };
  returnForm = { codtopdev: '', descTopdev: '', codnat: '', descNat: '', codcencus: '', descCencus: '', codtipvenda: '', descTipvenda: '', codprod: '', descProd: '', controle: '', codlocal: '', descLocal: '', qtddev: '', dtcotacao: 'N', condpecadev: '' };
  filters = { nota: '', parceiro: '', partNumber: '', dataInicial: '', dataFinal: '' };

  get statuses(): Array<{ value: string; label: string }> {
    const values = [...new Set(this.rows.map(row => String(row.STATUS ?? '').trim()).filter(Boolean))].sort();
    return [{ value: '', label: 'Todos' }, ...values.map(value => ({ value, label: this.statusLabel(value) }))];
  }

  constructor(private readonly gateway: SankhyaGatewayService) {}

  ngOnInit(): void { this.load(); }

  get filteredRows(): CoreRow[] {
    const term = this.normalize(this.search);
    return this.rows.filter(row => {
      const status = String(row.STATUS ?? '').trim().toUpperCase();
      const statusMatch = !this.statusFilter || status === this.statusFilter;
      const termMatch = !term || this.normalize([
        row.PARTNUMBER, row['Produto.GPL_PN_ALTERNATIVO'], row.CONTROLE,
        row['Parceiro.NOMEPARC'], row.NUNOTA, row.NUMNOTA, row.DESCRTIPCORE
      ].join(' ')).includes(term);
      const f = this.filters;
      const notaMatch = !f.nota || String(row.NUMNOTA ?? row.NUNOTA ?? '').includes(f.nota.trim());
      const partnerMatch = !f.parceiro || this.normalize(row['Parceiro.NOMEPARC']).includes(this.normalize(f.parceiro));
      const pnMatch = !f.partNumber || this.normalize(`${row.PARTNUMBER ?? ''} ${row['Produto.GPL_PN_ALTERNATIVO'] ?? ''}`).includes(this.normalize(f.partNumber));
      const date = this.dateValue(row.DTNEG);
      const from = f.dataInicial ? new Date(`${f.dataInicial}T00:00:00`).getTime() : 0;
      const to = f.dataFinal ? new Date(`${f.dataFinal}T23:59:59`).getTime() : Number.MAX_SAFE_INTEGER;
      const hasDateFilter = Boolean(f.dataInicial || f.dataFinal);
      const dateMatch = !hasDateFilter || Boolean(date && date >= from && date <= to);
      return statusMatch && termMatch && notaMatch && partnerMatch && pnMatch && dateMatch;
    });
  }

  get pageRows(): CoreRow[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredRows.slice(start, start + this.pageSize);
  }

  get pageCount(): number { return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize)); }
  get totalValue(): number { return this.filteredRows.reduce((sum, row) => sum + this.numberValue(row.VLRCORE), 0); }
  get availableQuantity(): number { return this.filteredRows.reduce((sum, row) => sum + this.numberValue(row.QTDDISPONIVEL ?? row.QTDNEG), 0); }

  load(): void {
    this.loading = true;
    this.error = '';
    this.gateway.buscarControleCoreNativo().subscribe({
      next: (response: any) => {
        const body = response?.responseBody ?? response?.serviceResponse?.responseBody ?? response;
        this.rows = (Array.isArray(body?.result) ? body.result : []).map((record: any) => this.toRow(record));
        this.page = 1;
        this.loading = false;
        if (response?.status && String(response.status) !== '1') {
          this.error = response?.statusMessage || 'Não foi possível consultar os registros de CORE.';
        }
      },
      error: (err: any) => {
        this.loading = false;
        this.error = err?.error?.message ?? err?.error?.erro ?? 'Falha ao consultar o Controle de Core. Tente novamente.';
      }
    });
  }

  applyFilters(): void { this.filtersOpen = false; this.page = 1; }
  clearFilters(): void {
    this.filters = { nota: '', parceiro: '', partNumber: '', dataInicial: '', dataFinal: '' };
    this.statusFilter = '';
    this.search = '';
    this.page = 1;
  }
  setStatus(value: string): void { this.statusFilter = value; this.page = 1; }
  changePage(offset: number): void { this.page = Math.min(this.pageCount, Math.max(1, this.page + offset)); }
  openDetails(row: CoreRow): void { this.detail = row; }
  closeDetails(): void { this.detail = null; }
  openActionModal(action: 'charge' | 'return', row: CoreRow): void {
    this.actionRow = row;
    this.actionModal = action;
  }
  closeActionModal(): void {
    this.actionModal = null;
    this.actionRow = null;
  }
  trackRow(index: number, row: CoreRow): string { return String(row.SEQCC ?? `${row.NUNOTA}-${row.SEQUENCIA}-${index}`); }

  statusLabel(value: unknown): string {
    const status = String(value ?? '').trim().toUpperCase();
    return status || 'Sem status';
  }

  statusClass(value: unknown): string {
    return 'neutral';
  }

  formatMoney(value: unknown): string {
    const amount = this.numberValue(value);
    return this.money.format(amount);
  }

  formatDate(value: unknown): string {
    if (!value) return '—';
    const parsed = this.dateValue(value);
    return parsed ? this.dates.format(new Date(parsed)) : String(value);
  }

  private toRow(record: any): CoreRow {
    if (!record || typeof record !== 'object') return {};
    if (Array.isArray(record.field)) {
      return Object.fromEntries(record.field.map((field: any) => [field.fieldName, field.$ ?? field.value ?? '']));
    }
    return Object.fromEntries(Object.entries(record).map(([key, value]: [string, any]) => [
      key, value && typeof value === 'object' && '$' in value ? value.$ : value
    ]));
  }

  private numberValue(value: unknown): number {
    if (typeof value === 'number') return value;
    const raw = String(value ?? '').trim();
    const text = raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw;
    const parsed = Number(text);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private dateValue(value: unknown): number {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    const text = String(value).trim();
    const br = text.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    const date = br ? new Date(Number(br[3]), Number(br[2]) - 1, Number(br[1])) : new Date(text);
    const time = date.getTime();
    return Number.isFinite(time) ? time : 0;
  }

  private normalize(value: unknown): string {
    return String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().trim();
  }
}
